import { useState, useEffect, useCallback, useRef } from 'react';
import { Grid, Header, Button, Popup } from 'semantic-ui-react';
import { v4 as uuidv4 } from 'uuid';
import { Formik, Form } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { useBookOperationsZoneBased } from '../../hooks/entities/useBookOperationsZoneBased';
import { useSharedData } from '../../hooks/core/useZoneHooks';
import { useCharacterZone } from '../../hooks/core/useZoneHooks';
import { fetchBookProfile } from '../../slices/bookWorksheetDataSlice';
import { bookValidationSchema } from '../../utils/validationSchemas';
import { DelayedMessage } from '../../utils';
import useMediaUpload from '../../../hooks/useMediaUpload';
import { apiClient } from '../../../apiTools/apiClient';
import { apiRoutes } from '../../../apiTools/constants/apiRoutes';
import {
  // FormikInput,
  // FormikTextArea,
  // FormikSelect,
  // FormikField,
  FormChangeDetector,
  FormikImageUploadAdapted
} from '../common';

import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
  FormikField
} from '../../../pages/subPages/voiceActor/FormComponents';

import { StatusForm } from '../../../components/statusTools/StatusIndicators';
import useStatusManagement from '../../../hooks/bookCreator/useStatusManagement';
import { SimpleValuesDisplay } from '../../../utils/aaiProfileTools';
import { CharactersListEnh } from '../lists';
import ShowStatusMessages from '../common/StatusMessageDisplay';
import '../../styles/bookFormEnhanced.css';

/**
 * Enhanced BookForm component
 * Form for creating and editing books with advanced features
 * @param {Object} props - Component props
 * @param {Function} props.onSave - Callback when the form is saved
 * @returns {JSX.Element} BookForm component
 */
const BookFormEnh = ({ onSave }) => {
  // Get data and functions from the new zone-based hooks
  const { selectedBook, createBook, updateBook, booksLoading, fetchBooks } = useBookOperationsZoneBased();
  const sharedData = useSharedData();
  const characterZone = useCharacterZone();

  // Import dispatch to call fetchBookProfile thunk directly
  const dispatch = useDispatch();

  // Extract needed data from shared data
  const { selections } = sharedData;
  const bookId = selections?.bookId;
  const litGroupId = selections?.litGroupId;

  // Get the book profile from sharedData
  const bookProfile = bookId ? sharedData.bookProfiles?.[bookId] : null;

  // Get character selection function
  const setCharacterSelection = characterZone.actions.selectCharacter;

  // const userProfile = useSelector((state) => state.user.profile);
  // const userId = userProfile?.data?.userId || 'default-user-id';
  const formikRef = useRef(null);

  // Track which books we've already attempted to fetch profiles for to prevent infinite loops
  const fetchedProfilesRef = useRef(new Set());

  // Local state
  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formChanged, setFormChanged] = useState(false);
  const [profileMissing, setProfileMissing] = useState(false);

  // Author dropdown state
  const [availableAuthors, setAvailableAuthors] = useState([]);
  const [isNewAuthor, setIsNewAuthor] = useState(false);
  const [authorLoading, setAuthorLoading] = useState(false);

  // Use the useMediaUpload hook
  // const { uploadMedia, isUploading, error: uploadError, reset: resetUpload } = useMediaUpload();
  const { uploadMedia, isUploading: isAvatarUploading, error: avatarUploadError, reset: resetUpload } = useMediaUpload();

  // Get user ID for uploads
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.data?.userId || 'default-user-id';

  // Initialize status management hook
  const statusManagement = useStatusManagement(selectedBook, formikRef);
  const {
    priority, setPriority,
    status, setStatus,
    deadline, setDeadline,
    priorityOptions, statusOptions
  } = statusManagement;

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Check if the selected book has a missing profile bookProfile
  useEffect(() => {
    if (selectedBook?.profileMissing || (selectedBook && !selectedBook.name && !selectedBook.profile?.description)) {
      setProfileMissing(true);
    } else {
      setProfileMissing(false);
    }
  }, [selectedBook]);

  // Debug: Log selectedBook changes
  useEffect(() => {
    console.log('[BookForm-enh] selectedBook changed:', selectedBook);
    console.log('[BookForm-enh] selectedBook keys:', selectedBook ? Object.keys(selectedBook) : 'null');
    console.log('[BookForm-enh] selectedBook.profile.description:', selectedBook?.description);
    console.log('[BookForm-enh] selectedBook.author:', selectedBook?.author);
    console.log('[BookForm-enh] bookId:', bookId);
    console.log('[BookForm-enh] litGroupId:', litGroupId);
  }, [selectedBook, bookId, litGroupId]);

  // Fetch book profile when book is selected but missing profile data
  useEffect(() => {
    if (bookId && selectedBook && (!selectedBook.profile?.description && !selectedBook.profile?.author) && !selectedBook.profile) {
      // Check if we've already attempted to fetch this book's profile
      if (!fetchedProfilesRef.current.has(bookId)) {
        console.log('[BookForm-enh] Book selected but missing profile data, fetching book profile for:', bookId);
        fetchedProfilesRef.current.add(bookId);
        dispatch(fetchBookProfile(bookId));
      } else {
        console.log('[BookForm-enh] Already attempted to fetch profile for book:', bookId, '- skipping to prevent infinite loop');
      }
    }
  }, [bookId, selectedBook, dispatch]);

  // Clear the fetched profiles tracking when literary group changes
  useEffect(() => {
    fetchedProfilesRef.current.clear();
  }, [litGroupId]);

  // Generate bookId early for new books
  useEffect(() => {
    // Detect "new book" mode: litGroupId exists, but no bookId and no selectedBook
    if (litGroupId && !bookId && !selectedBook) {
      console.log('[BookForm-enh] Detected new book mode - generating early bookId');
      const newBookId = uuidv4();
      console.log('[BookForm-enh] Generated bookId for new book:', newBookId);

      // Set the bookId in shared data selections
      sharedData.actions.setBookSelection(newBookId);
    }
  }, [litGroupId, bookId, selectedBook, sharedData.actions]);

  // Fetch authors when Literary Group selected
  useEffect(() => {
    if (litGroupId) {
      setAuthorLoading(true);
      const endpoint = apiRoutes.LIT_GROUPS.AUTHORS.replace(':litGroupId', litGroupId);

      apiClient.get(endpoint)
        .then(response => {
          setAvailableAuthors(response.data.authors || []);
        })
        .catch(error => {
          console.error('Error fetching authors:', error);
          setAvailableAuthors([]);
        })
        .finally(() => {
          setAuthorLoading(false);
        });
    }
  }, [litGroupId]);

  // Validate form data before submission
  const validateFormData = useCallback((values) => {
    // Check required fields
    if (!values.name || !values.name.trim()) {
      return 'Book Title is required';
    }
    if (!values.description || !values.description.trim()) {
      return 'Description is required';
    }

    // Check author - since we auto-populate the dropdown, just check if author is selected
    if (!values.author || !values.author.trim()) {
      return 'Author is required';
    }

    return null; // No errors
  }, []);

  // Handle form submission
  const handleSubmit = async (values) => {
    setFormLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    // setUploadError(null);

    // Validate form data
    const validationError = validateFormData(values);
    if (validationError) {
      setErrorMessage(validationError);
      setFormLoading(false);
      return;
    }

    try {
      // Handle book cover upload if there's a new file
      let bookCoverUrl = values.bookCoverImageUrl;

      if (values.hasNewImageFile && values.bookCoverImageFile) {
        try {
          resetUpload(); // Reset any previous upload state
          console.log('Uploading new book cover image...');

          // Use the useMediaUpload hook to upload the file
          bookCoverUrl = await uploadMedia(
            values.bookCoverImageFile,
            'bookCoverProfileImage',
            {
              bookId: bookId,
              litGroupId: litGroupId,
              resourceType: 'bookCoverProfileImage'
            },
            userId
          );

          console.log('Book cover uploaded successfully, URL:', bookCoverUrl);
        } catch (error) {
          console.error('Error handling book cover:', error);
          setErrorMessage('Failed to upload book cover: ' + (error.message || 'Unknown error'));
          setFormLoading(false);
          return;
        }
      }

      // Prepare book data for API
      const bookData = {
        name: values.name,
        title: values.name, // Always set title equal to name for consistency
        description: values.description,
        author: values.author,  // ✅ ADD THIS - Pass the author field through
        // Send separate first and last names for new authors, or use existing author name
        authorFirstName: isNewAuthor ? values.authorFirstName : (availableAuthors.find(a => a.name === values.author)?.firstName || ''),
        authorLastName: isNewAuthor ? values.authorLastName : (availableAuthors.find(a => a.name === values.author)?.lastName || ''),
        series: values.series,
        priority: values.priority,    // ✅ From Formik
        status: values.status,        // ✅ From Formik
        deadline: values.deadline,    // ✅ From Formik
        bookCover: bookCoverUrl,
        litGroupId: litGroupId, // Important for normalized structure
        profileMissing: false // Mark as having a profile since we're saving profile data
      };

      let result;

      // Determine if this is a create or update operation
      // CREATE: No selectedBook exists (new book, even if bookId exists from early generation)
      // UPDATE: selectedBook exists with UUID (editing existing book)
      if (bookId && selectedBook && selectedBook.uuid) {
        // Update existing book - book exists in database
        console.log('[BookForm-enh] Updating existing book:', bookId);
        result = await updateBook({
          bookId,
          data: bookData
        });
        setSuccessMessage('Book updated successfully!');
      } else if (litGroupId) {
        // Create new book - pass pre-generated bookId if available
        console.log('[BookForm-enh] Creating new book with bookId:', bookId);
        result = await createBook({
          litGroupId,
          data: bookData,
          bookId: bookId // Pass pre-generated bookId from early generation
        });
        setSuccessMessage('Book created successfully!');
      } else {
        throw new Error('No literary group selected');
      }

      setFormChanged(false);

      // Call the onSave callback if provided
      if (onSave) {
        onSave(result);
      }

      // Refresh the books list
      if (litGroupId) {
        fetchBooks(litGroupId);
      }
    } catch (err) {
      setErrorMessage(err.message || 'An error occurred while saving the book');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle character selection
  const handleCharacterSelect = (character) => {
    if (character && character.uuid) {
      setCharacterSelection(character.uuid);
    }
  };

  // Handle author selection change
  const handleAuthorChange = (e, { value }) => {
    if (value === '__NEW_AUTHOR__') {
      setIsNewAuthor(true);
      formikRef.current.setFieldValue('author', '');
      formikRef.current.setFieldValue('authorFirstName', '');
      formikRef.current.setFieldValue('authorLastName', '');
    } else {
      setIsNewAuthor(false);
      formikRef.current.setFieldValue('author', value);
      // Find the selected author to get first and last names
      const selectedAuthor = availableAuthors.find(author => author.name === value);
      if (selectedAuthor) {
        formikRef.current.setFieldValue('authorFirstName', selectedAuthor.firstName || '');
        formikRef.current.setFieldValue('authorLastName', selectedAuthor.lastName || '');
      }
    }
  };

  // Handle first/last name changes and auto-populate dropdown
  const handleNameChange = (field, value) => {
    formikRef.current.setFieldValue(field, value);

    // Get current values
    const currentValues = formikRef.current.values;
    const firstName = field === 'authorFirstName' ? value : currentValues.authorFirstName;
    const lastName = field === 'authorLastName' ? value : currentValues.authorLastName;

    // If both names are filled, create full name and add to dropdown
    if (firstName && firstName.trim() && lastName && lastName.trim()) {
      const fullName = `${firstName.trim()} ${lastName.trim()}`;

      // Check if this author already exists in the dropdown
      const existingAuthor = availableAuthors.find(author =>
        author.firstName?.toLowerCase() === firstName.trim().toLowerCase() &&
        author.lastName?.toLowerCase() === lastName.trim().toLowerCase()
      );

      if (!existingAuthor) {
        // Add new author to dropdown temporarily
        const tempAuthor = {
          uuid: 'temp-' + Date.now(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          name: fullName
        };

        setAvailableAuthors(prev => [...prev, tempAuthor]);
      }

      // Set the author dropdown value to the full name
      formikRef.current.setFieldValue('author', fullName);
    }
  };

  // Get initial values for the form
  const getInitialValues = () => {
    // Use bookProfile from sharedData if available, otherwise fallback to selectedBook
    const bookName = bookProfile?.name || selectedBook?.name || '';

    return {
      name: bookName,
      // Prioritize bookProfile from sharedData, then check selectedBook properties
      description: bookProfile?.description || selectedBook?.description || selectedBook?.profile?.description || '',
      author: bookProfile?.author || selectedBook?.author || selectedBook?.profile?.author || '',
      authorFirstName: bookProfile?.authorFirstName || selectedBook?.authorFirstName || selectedBook?.profile?.authorFirstName || '',
      authorLastName: bookProfile?.authorLastName || selectedBook?.authorLastName || selectedBook?.profile?.authorLastName || '',
      series: bookProfile?.series || selectedBook?.series || selectedBook?.profile?.series || '',
      // ✅ ONLY these (correct lowercase defaults):
      priority: bookProfile?.priority || selectedBook?.priority || selectedBook?.profile?.priority || 'medium',
      status: bookProfile?.status || selectedBook?.status || selectedBook?.profile?.status || 'not-started',
      deadline: bookProfile?.deadline || selectedBook?.deadline || selectedBook?.profile?.deadline || null,
      bookCoverImageUrl: bookProfile?.bookCover || selectedBook?.bookCover || selectedBook?.profile?.bookCover || '',
      bookCoverImageFile: null,
      hasNewImageFile: false
    };
  };

  // Show message if no literary group is selected
  if (!litGroupId) {
    return (
      <DelayedMessage
        visible={true}
        type="info"
        header="No Literary Group Selected"
        delay={200}
      >
        <p>Please select a literary group first to create or edit a book.</p>
      </DelayedMessage>
    );
  }

  return (
    <div style={{ width: "1024px !important" }}>
      <Header as="h3" dividing>
        {selectedBook?.uuid ? 'Edit Book Profile' : 'Create New Book Profile'}
      </Header>

      <DelayedMessage
        visible={profileMissing}
        type="warning"
        header="Missing Book Profile"
        delay={500}
      >
        <p>This book doesn't have a profile in the database. Please add the required information and save to create a profile.</p>
      </DelayedMessage>

      <Formik
        innerRef={formikRef}
        initialValues={getInitialValues()}
        validationSchema={bookValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, values, errors, touched, isValid, dirty }) => (
          <Form>
            <FormChangeDetector
              onChange={setFormChanged}
              initialValues={getInitialValues()}
            />
            <div className="book-profile-img">
              <FormikImageUploadAdapted
                urlFieldName="bookCoverImageUrl"
                fileFieldName="bookCoverImageFile"
                flagFieldName="hasNewImageFile"
                resourceType="bookCoverProfileImage"
                label="Book Cover"
                hideInputFields={true}

                onImageChange={(file) => {
                  // Set the bookCoverImageFile field
                  formikRef.current.setFieldValue('bookCoverImageFile', file);
                  // Set the hasNewImageFile flag
                  formikRef.current.setFieldValue('hasNewImageFile', true);
                }}
              />
            </div>
            <Grid divided={false}>
              {/* Book Information */}
              <Grid.Row className="">
                <Grid.Column width={10}>
                  <FormikField
                    name="name"
                    component={FormikInput}
                    label="Book Title"
                    placeholder="Enter book title"
                    fluid
                    value={values.name}

                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row className="noTopPad">
                <Grid.Column width={16}>
                  <FormikField
                    className=""
                    name="description"
                    component={FormikTextArea}
                    label="Description"
                    placeholder="Enter a description for this book"
                    rows={4}
                    value={values.description}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row className="noTopPad">
                <Grid.Column width={8}>
                  <FormikField
                    name="author"
                    component={FormikSelect}
                    label="Author"
                    placeholder={authorLoading ? "Loading authors..." : "Select or add author"}
                    loading={authorLoading}
                    options={[
                      ...availableAuthors.map(author => ({
                        key: author.uuid,
                        text: author.name,
                        value: author.name
                      })),
                      { key: 'new', text: '+ Add New Author', value: '__NEW_AUTHOR__' }
                    ]}
                    onChange={handleAuthorChange}
                    value={values.author}
                    fluid
                  />

                  {isNewAuthor && (
                    <div style={{ marginTop: '10px' }}>
                      <Grid>
                        <Grid.Row>
                          <Grid.Column width={8}>
                            <FormikField
                              name="authorFirstName"
                              component={FormikInput}
                              label="First Name"
                              placeholder="Enter first name"
                              fluid
                              onChange={(e, data) => {
                                const value = data?.value || e?.target?.value || '';
                                handleNameChange('authorFirstName', value);
                              }}
                            />
                          </Grid.Column>
                          <Grid.Column width={8}>
                            <FormikField
                              name="authorLastName"
                              component={FormikInput}
                              label="Last Name"
                              placeholder="Enter last name"
                              fluid
                              onChange={(e, data) => {
                                const value = data?.value || e?.target?.value || '';
                                handleNameChange('authorLastName', value);
                              }}
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </div>
                  )}
                </Grid.Column>
                <Grid.Column width={8} className="noLeftPad">
                  <FormikField
                    name="series"
                    component={FormikInput}
                    label="Series (Optional)"
                    placeholder="Enter series name"
                    fluid
                    value={values.series}

                  />
                </Grid.Column>
              </Grid.Row>

              {/* Book Cover */}


              {/* Status Information */}
              <Grid.Row className="noTopPad">
                <Grid.Column width={16}>
                  <Header as="h4" dividing>Status Information</Header>
                  <div style={{ position: 'relative', zIndex: 10 }}>
                    <StatusForm
                      charName={selectedBook?.name || 'Book'}
                      priority={priority}
                      setPriority={setPriority}
                      status={status}
                      setStatus={setStatus}
                      deadline={deadline}
                      setDeadline={setDeadline}
                      priorityOptions={priorityOptions}
                      statusOptions={statusOptions}
                    />
                  </div>
                </Grid.Column>
              </Grid.Row>
              <ShowStatusMessages
                isAvatarUploading={isAvatarUploading}
                avatarUploadError={avatarUploadError}
                successMessage={successMessage}
                errorMessage={errorMessage}
              />
              {/* Messages */}
              {/* <Grid.Row className="noFiveTopNoBottomPad">
                <Grid.Column width={16}>
                  <DelayedMessage
                    visible={isAvatarUploading}
                    type="info"
                    delay={100} // Short delay for uploading message
                  >
                    <Icon name="spinner" loading />
                    <span>Uploading image...</span>
                  </DelayedMessage>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row className="noFiveTopNoBottomPad">
                <Grid.Column width={16}>
                  <DelayedMessage
                    visible={!!avatarUploadError}
                    type="negative"
                    header="Upload Error"
                    delay={300}
                  >
                    <p>{typeof avatarUploadError === 'string' ? avatarUploadError : avatarUploadError?.message || 'An unknown error occurred'}</p>
                  </DelayedMessage>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16}>
                  <DelayedMessage
                    visible={!!successMessage}
                    type="positive"
                    header="Success"
                    delay={0} // No delay for success messages
                  >
                    <p>{successMessage}</p>
                  </DelayedMessage>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16}>
                  <DelayedMessage
                    visible={!!errorMessage}
                    type="negative"
                    header="Error"
                    delay={300}
                  >
                    <p>{errorMessage}</p>
                  </DelayedMessage>
                </Grid.Column>
              </Grid.Row> */}

              {/* Form Buttons */}
              <Grid.Row className="noTopPad">
                <Grid.Column width={16}>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Popup
                      className="high-z-popup"  // Add custom class
                      content={
                        <SimpleValuesDisplay
                          key={Math.random().toString()}
                          values={values}
                          errors={errors}
                          touched={touched}
                          isFormChanged={formChanged}
                        />
                      }

                      // content={
                      //   isSubmitting ? 'Submitting...' :
                      //     !values.name ? 'Book Title is required' :
                      //       !values.description ? 'Description is required' :
                      //         !values.author ? 'Author is required' :
                      //           !isValid ? 'Please fix validation errors' :
                      //             !formChanged ? 'No changes to save' :
                      //               'Ready to submit'
                      // }
                      trigger={
                        <span>
                          <Button
                            primary
                            type="submit"
                            loading={formLoading || booksLoading === 'loading'}
                            disabled={
                              isSubmitting ||
                              isAvatarUploading ||
                              !values.name ||
                              !values.description ||
                              !values.author ||
                              !formChanged || // Disable if no changes have been made
                              !isValid
                            }
                          >
                            {bookId ? 'Update Book' : 'Create Book'}
                          </Button>
                        </span>
                      }
                      position="top left"
                      size="tiny"

                    />

                    {/* Only show the Cancel Changes button when changes have been made */}
                    {formChanged && (
                      <Popup
                        className='values-display-popup'
                        content="Discard all changes and reset the form"
                        trigger={
                          <Button
                            type="button"
                            onClick={() => {
                              formikRef.current.resetForm();
                              setFormChanged(false);
                            }}
                          >
                            Cancel Changes
                          </Button>
                        }
                        position="top left"
                        size="tiny"
                      />
                    )}
                  </div>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        )}
      </Formik>

      {/* Characters list for the selected book */}
      {/* {bookId && ( */}
      <div style={{ marginTop: '20px' }}>
        <Header as="h3" dividing>Characters</Header>
        <CharactersListEnh onCharacterSelect={handleCharacterSelect} />
      </div>
      {/* )} */}
    </div>
  );
};

export default BookFormEnh;
